import React, {FC, useCallback, useState} from 'react';

import {Renderer, RendererOf} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {useConversationStatusBulkActionPermission} from '../../../../../../../srcCommon/permissions/useBulkActionPermission';
import {useConversationStatusActionOfflinePermission} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {SafetyNetActionsEnum} from '../../safetyNets/safetyNetsContext';
import {SafetyNetsCoordinator} from '../../safetyNets/safetyNetsCoordinator';
import {TriageTeamConfirmationDialogContainer} from '../../topbar/triageTeamConfirmationDialogContainer';
import {BulkActionConfirmationDialog} from '../bulk/bulkActionConfirmationDialog';

/*
 * Props.
 */

interface ConversationsArchiveCoordinatorProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  inboxesParameters?: InboxesParameters;
  contextNamespaces: ReadonlyArray<string>;
  areAllArchived: boolean;
  shouldShowArchiveTeamAlert: boolean;
  onArchive: () => void;
  onReopen: () => void;
  onBulkArchive: () => void;
  onBulkReopen: () => void;
  isBulkActionRequired: boolean;

  renderButton: RendererOf<ConversationsArchiveButtonProps>;
}

export interface ConversationsArchiveButtonProps {
  areAllArchived: boolean;
  permissionResult: PermissionResult;
  onClick: () => void;
  maybeRenderArchiveLabel?: Renderer;
}

/*
 * Component.
 */

export const ConversationsArchiveCoordinator: FC<ConversationsArchiveCoordinatorProps> = (props) => {
  const {
    cellsContext,
    conversations,
    contextNamespaces,
    areAllArchived,
    inboxesParameters,
    isBulkActionRequired,
  } = props;
  const [isArchiveTeamConfirmationOpen, setIsArchiveTeamConfirmationOpen] = useState(false);
  const [isBulkArchiveConfirmationOpen, setIsBulkArchiveConfirmationOpen] = useState(false);
  const [isBulkReopenConfirmationOpen, setIsBulkReopenConfirmationOpen] = useState(false);

  const offlinePermission = useConversationStatusActionOfflinePermission(cellsContext, conversations);
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    isBulkActionRequired,
    inboxesParameters,
  );

  /*
   * Event handlers.
   */

  const {shouldShowArchiveTeamAlert, onArchive, onBulkArchive} = props;

  const onArchiveClick = useCallback(() => {
    // If we're doing a bulk action, confirm first.
    if (isBulkActionRequired) {
      setIsBulkArchiveConfirmationOpen(true);
      return;
    }

    if (shouldShowArchiveTeamAlert) {
      setIsArchiveTeamConfirmationOpen(true);
      return;
    }

    // Otherwise, close conversation immediately.
    onArchive();
  }, [
    shouldShowArchiveTeamAlert,
    isBulkActionRequired,
    setIsArchiveTeamConfirmationOpen,
    setIsBulkArchiveConfirmationOpen,
    onArchive,
  ]);

  const onConfirmTeamArchiveClick = useCallback(() => {
    setIsArchiveTeamConfirmationOpen(false);
    onArchive();
  }, [setIsArchiveTeamConfirmationOpen, onArchive]);

  const onConfirmBulkArchiveClick = useCallback(() => {
    setIsBulkArchiveConfirmationOpen(false);
    onBulkArchive();
  }, [setIsBulkArchiveConfirmationOpen, onBulkArchive]);

  const {onReopen, onBulkReopen} = props;
  const onReopenClick = useCallback(() => {
    // If we're doing a bulk action, confirm first.
    if (isBulkActionRequired) {
      setIsBulkReopenConfirmationOpen(true);
      return;
    }

    // Otherwise, reopen conversation immediately.
    onReopen();
  }, [isBulkActionRequired, onReopen, setIsBulkReopenConfirmationOpen]);

  const onConfirmBulkReopenClick = useCallback(() => {
    setIsBulkReopenConfirmationOpen(false);
    onBulkReopen();
  }, [setIsBulkReopenConfirmationOpen, onBulkReopen]);

  /*
   * Render.
   */

  // If the conversations are archived, render the filled icon.
  if (areAllArchived) {
    return (
      <>
        <PermissionCheck
          namespaces={contextNamespaces}
          permission={PermissionsEnum.CONVERSATION_ARCHIVE}
          offlinePermission={offlinePermission}
          bulkActionPermission={bulkActionPermission}
        >
          {(permissionResult) =>
            props.renderButton({areAllArchived: true, permissionResult, onClick: onReopenClick})
          }
        </PermissionCheck>
        {isBulkReopenConfirmationOpen && (
          <BulkActionConfirmationDialog
            onConfirmClick={onConfirmBulkReopenClick}
            onRequestClose={() => setIsBulkReopenConfirmationOpen(false)}
            actionType={BulkActionTypesEnum.REOPEN}
          />
        )}
      </>
    );
  }

  // Otherwise, render the normal icon.
  return (
    <>
      <PermissionCheck
        namespaces={contextNamespaces}
        permission={PermissionsEnum.CONVERSATION_ARCHIVE}
        offlinePermission={offlinePermission}
        bulkActionPermission={bulkActionPermission}
      >
        {(permissionResult) => (
          <SafetyNetsCoordinator
            conversations={conversations}
            onActionValidated={onArchiveClick}
            safetyNetActionType={SafetyNetActionsEnum.ARCHIVE}
            cellsContext={cellsContext}
          >
            {(onClick) =>
              props.renderButton({
                areAllArchived: false,
                permissionResult,
                onClick,
              })
            }
          </SafetyNetsCoordinator>
        )}
      </PermissionCheck>
      {isArchiveTeamConfirmationOpen && (
        <TriageTeamConfirmationDialogContainer
          conversations={conversations}
          cellsContext={cellsContext}
          onConfirmClick={onConfirmTeamArchiveClick}
          onRequestClose={() => setIsArchiveTeamConfirmationOpen(false)}
          action={TriageActionsEnum.ARCHIVE}
        />
      )}
      {isBulkArchiveConfirmationOpen && (
        <BulkActionConfirmationDialog
          onConfirmClick={onConfirmBulkArchiveClick}
          onRequestClose={() => setIsBulkArchiveConfirmationOpen(false)}
          actionType={BulkActionTypesEnum.ARCHIVE}
        />
      )}
    </>
  );
};
